import { useCallback, useEffect, useState } from 'react';
import { fetchProducts, fetchCategories, fetchBrands } from '../services/productService';

let cachedData = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000;
let inFlightPromise = null;

const sanitizeArray = (value) => (Array.isArray(value) ? value : []);

const buildFallbackCatalog = (overrides = {}) => ({
  products: [],
  categories: [],
  brands: [],
  isMock: true,
  error: null,
  ...overrides,
});

const loadCatalogData = async () => {
  if (inFlightPromise) {
    return inFlightPromise;
  }
  inFlightPromise = Promise.all([fetchProducts(), fetchCategories(), fetchBrands()])
    .then(([products, categories, brands]) => {
      const sanitizedProducts = sanitizeArray(products);
      const sanitizedCategories = sanitizeArray(categories);
      const sanitizedBrands = sanitizeArray(brands);

      cachedData = {
        products: sanitizedProducts,
        categories: sanitizedCategories,
        brands: sanitizedBrands,
        isMock: !(sanitizedProducts.length && sanitizedCategories.length && sanitizedBrands.length),
        error: null,
      };
      cacheTimestamp = Date.now();
      return cachedData;
    })
    .catch((error) => {
      cachedData = buildFallbackCatalog({ error });
      cacheTimestamp = Date.now();
      return cachedData;
    })
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
};

export const useCatalogData = () => {
  const [state, setState] = useState(() => {
    if (cachedData && Date.now() - cacheTimestamp < CACHE_TTL) {
      return {
        ...cachedData,
        loading: false,
        error: cachedData.error ?? null,
      };
    }
    return {
      ...buildFallbackCatalog(),
      loading: true,
      error: null,
    };
  });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const shouldRefresh = !cachedData || Date.now() - cacheTimestamp > CACHE_TTL;
      if (!shouldRefresh) {
        setState({
          ...cachedData,
          loading: false,
          error: cachedData.error ?? null,
        });
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      const data = await loadCatalogData();
      if (!cancelled) {
        setState({
          ...data,
          loading: false,
          error: data.error ?? null,
        });
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));
    cachedData = null;
    cacheTimestamp = 0;
    const data = await loadCatalogData();
    setState({
      ...data,
      loading: false,
      error: data.error ?? null,
    });
  }, []);

  return {
    products: state.products,
    categories: state.categories,
    brands: state.brands,
    loading: state.loading,
    error: state.error,
    isMock: state.isMock,
    refresh,
  };
};



